import { z } from 'zod';

export const CLASSIFIER_POLICY_VERSION = 'jev-1.13.0/assist-routing-v2';
export const classificationLabelsSchema = z
  .object({
    paymentClaim: z.boolean(),
    dispute: z.boolean(),
    stop: z.boolean(),
    needsHuman: z.boolean(),
  })
  .strict();
export type ClassificationLabels = z.infer<typeof classificationLabelsSchema>;
export interface ReviewedClassification {
  conversationId: string;
  predicted: ClassificationLabels;
  expected: ClassificationLabels;
}
export interface ClassifierSignalCounts {
  signal: keyof ClassificationLabels;
  positives: number;
  truePositives: number;
  falsePositives: number;
  falseNegatives: number;
}
export function requiredClassifierSignals(
  capabilities: readonly string[]
): readonly (keyof ClassificationLabels)[] {
  return capabilities.includes('collections')
    ? ['paymentClaim', 'dispute', 'stop', 'needsHuman']
    : ['stop', 'needsHuman'];
}
export function assessClassifierCounts(
  conversations: number,
  reviewedMessages: number,
  counts: readonly ClassifierSignalCounts[],
  required: readonly (keyof ClassificationLabels)[] = [
    'paymentClaim',
    'dispute',
    'stop',
    'needsHuman',
  ]
) {
  const signals = counts.map((signal) => ({
    ...signal,
    required: required.includes(signal.signal),
    passed:
      signal.positives >= 20 &&
      signal.falseNegatives === 0 &&
      signal.truePositives + signal.falsePositives > 0 &&
      signal.truePositives * 100 >=
        (signal.truePositives + signal.falsePositives) * 95,
  }));
  return {
    policyVersion: CLASSIFIER_POLICY_VERSION,
    conversations,
    reviewedMessages,
    signals,
    passed:
      conversations >= 100 &&
      signals.length === 4 &&
      signals.filter((signal) => signal.required).length === required.length &&
      signals.every((signal) => !signal.required || signal.passed),
  };
}

export function assessClassifierQuality(
  cases: readonly ReviewedClassification[],
  capabilities: readonly string[] = ['collections']
) {
  const conversations = new Set(cases.map((item) => item.conversationId)).size;
  const signals = (
    ['paymentClaim', 'dispute', 'stop', 'needsHuman'] as const
  ).map((signal) => {
    const positives = new Set(
      cases
        .filter((item) => item.expected[signal])
        .map((item) => item.conversationId)
    ).size;
    let truePositives = 0,
      falsePositives = 0,
      falseNegatives = 0;
    for (const item of cases) {
      if (item.predicted[signal] && item.expected[signal]) truePositives += 1;
      if (item.predicted[signal] && !item.expected[signal]) falsePositives += 1;
      if (!item.predicted[signal] && item.expected[signal]) falseNegatives += 1;
    }
    return { signal, positives, truePositives, falsePositives, falseNegatives };
  });
  // Keep the imported thresholds for each required signal; enabled capabilities select which signals gate automation.
  return assessClassifierCounts(
    conversations,
    cases.length,
    signals,
    requiredClassifierSignals(capabilities)
  );
}
